import React from 'react';
import { Badge } from '../ui/badge';
import { Buildings, EnvelopeSimple, MapPin, TreeStructure, Users } from '@phosphor-icons/react';
import { ROLE_COLORS } from '../../utils/organizationChart';

const UserOrganizationCard = ({ node, compact = false, onNavigate }) => {
  const initials = (node.name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  const roleClass = ROLE_COLORS[node.role] || 'bg-slate-100 text-slate-700';
  const widthClass = compact ? 'w-[14rem]' : 'w-64';

  return (
    <button
      type="button"
      onClick={() => onNavigate?.(node)}
      className={`text-left bg-white border-2 border-slate-200 rounded-xl shadow-md hover:shadow-lg hover:border-blue-300 transition-all ${widthClass} ${compact ? 'p-3' : 'p-4'}`}
      data-testid={`org-chart-card-${node.id}`}
    >
      <div className="flex items-start gap-2.5">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-300 flex items-center justify-center text-xs font-bold text-slate-700 shrink-0">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-1 flex-wrap">
            <p className="font-bold text-slate-900 text-sm leading-snug">{node.name}</p>
            {!node.isActive && (
              <Badge variant="outline" className="text-[9px] text-red-600 border-red-200 px-1 py-0">Inactive</Badge>
            )}
          </div>
          {node.title && (
            <p className="text-[11px] text-slate-600 mt-0.5 line-clamp-2">{node.title}</p>
          )}
          <Badge className={`${roleClass} text-[9px] mt-1.5 px-1.5 py-0`}>
            {node.roleLabel || node.role?.replace(/_/g, ' ')}
          </Badge>
        </div>
      </div>

      {compact ? (
        <div className="mt-2 pt-2 border-t border-slate-100 space-y-1 text-[10px] text-slate-500">
          {node.team && (
            <div className="flex items-center gap-1 truncate">
              <Users size={11} className="shrink-0 text-blue-500" />
              <span className="font-medium text-slate-700">{node.team}</span>
            </div>
          )}
          {node.organization && (
            <div className="flex items-center gap-1 truncate">
              <Buildings size={11} className="shrink-0" />
              <span>{node.organization}</span>
            </div>
          )}
          {node.directReportsCount > 0 && (
            <div className="flex items-center gap-1 text-blue-600 font-medium">
              <TreeStructure size={11} />
              <span>{node.directReportsCount} report{node.directReportsCount === 1 ? '' : 's'}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 space-y-1.5 text-xs text-slate-500">
          {node.team && (
            <div className="flex items-center gap-1.5 truncate">
              <Users size={12} />
              <span>{node.team}</span>
            </div>
          )}
          {node.organization && (
            <div className="flex items-center gap-1.5 truncate">
              <Buildings size={12} />
              <span>{node.organization}</span>
            </div>
          )}
          {node.workLocation && (
            <div className="flex items-center gap-1.5 truncate">
              <MapPin size={12} />
              <span>{node.workLocation}{node.locationType && node.locationType !== 'unknown' ? ` (${node.locationType})` : ''}</span>
            </div>
          )}
          {node.email && (
            <div className="flex items-center gap-1.5 truncate">
              <EnvelopeSimple size={12} />
              <span>{node.email}</span>
            </div>
          )}
          {node.managerName && (
            <div className="flex items-center gap-1.5 truncate">
              <TreeStructure size={12} />
              <span>Reports to {node.managerName}</span>
            </div>
          )}
          {node.directReportsCount > 0 && (
            <div className="flex items-center gap-1.5">
              <Users size={12} />
              <span>{node.directReportsCount} direct report{node.directReportsCount === 1 ? '' : 's'}</span>
            </div>
          )}
        </div>
      )}
    </button>
  );
};

export default UserOrganizationCard;
